import { createContext, useState, ReactNode, useEffect, useMemo, useCallback } from 'react';
import { DeviationDto } from 'src/generated/services/TFinancialApi';
import { useShipmentSelector } from '../../ShipmentSelector';

type TContext = {
  selectedDeviation: DeviationDto | null;
  setSelectedDeviation: React.Dispatch<React.SetStateAction<DeviationDto | null>>;
  currentDeviationIndex: number;
  setSelectedNextDeviation: () => void;
};

export const SelectedDeviationContext = createContext<TContext | null>(null);

interface SelectedDeviationProviderProps {
  children: ReactNode;
}

export const SelectedDeviationProvider = ({ children }: SelectedDeviationProviderProps) => {
  const { shipment } = useShipmentSelector();
  const deviations = useMemo(() => shipment?.deviations ?? [], [shipment?.deviations]);

  const [selectedDeviation, setSelectedDeviation] = useState<DeviationDto | null>(deviations?.length > 0 ? deviations[0] : null);

  const currentDeviationIndex = useMemo(() => deviations.findIndex((deviation) => deviation.id === selectedDeviation?.id), [deviations, selectedDeviation]);

  // Handler to navigate to next deviation
  const setSelectedNextDeviation = useCallback(() => {
    // Find next deviation where isCurrentUserRequiredToFill is true
    for (let i = currentDeviationIndex + 1; i < deviations.length; i += 1) {
      if (deviations[i].isCurrentUserRequiredToFill === true) {
        setSelectedDeviation(deviations[i]);
        return;
      }
    }
    // If no deviation with isCurrentUserRequiredToFill = true found, do nothing
  }, [currentDeviationIndex, deviations, setSelectedDeviation]);
  // Update selected deviation when deviations change
  useEffect(() => {
    if (selectedDeviation) {
      // Try to find the same deviation in the new list
      const found = deviations.find((deviation) => deviation.id === selectedDeviation.id);
      setSelectedDeviation(found || null);
    } else if (deviations?.length > 0) {
      setSelectedDeviation(deviations[0]);
    } else {
      setSelectedDeviation(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [deviations]);

  // Reset to first deviation when shipment changes
  useEffect(() => {
    if (shipment?.id && deviations?.length > 0) {
      setSelectedDeviation(deviations[0]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [shipment?.id]);

  return (
    <SelectedDeviationContext.Provider
      value={{
        selectedDeviation,
        setSelectedDeviation,
        currentDeviationIndex,
        setSelectedNextDeviation,
      }}
    >
      {children}
    </SelectedDeviationContext.Provider>
  );
};
